import { ICrosshairConfig } from "../interfaces/interface";
import { DEFAULT_OG_IMAGE, DEFAULT_SITE_NAME, SITE_CANONICAL_ORIGIN } from "./constants";
import { DEFAULT_LOCALE, Locale, localizedPath } from "./locales";
import { getUiStrings } from "./uiStrings";
import { describeCrosshair } from "./crosshairInfo";

/**
 * Structured-data builders shared by the route components.
 *
 * The server prerenders the same shapes into the first HTML response
 * (see web/seo-content.mjs); these keep the hydrated DOM in step with it.
 */

const ORGANIZATION = {
    "@type": "Organization",
    name: DEFAULT_SITE_NAME,
    url: SITE_CANONICAL_ORIGIN,
    logo: `${SITE_CANONICAL_ORIGIN}/logo512.webp`,
};

/** The site itself, on the home page — where engines look for the publisher's identity. */
export function buildOrganizationJsonLd(): string {
    return JSON.stringify({ "@context": "https://schema.org", ...ORGANIZATION });
}

/**
 * Absolute URL of a detail page, inside the given locale's tree. A locale-blind URL here
 * makes a translated page's structured data cite the English page instead of itself.
 */
export function crosshairUrl(
    c: Pick<ICrosshairConfig, "id" | "slug">,
    locale: Locale = DEFAULT_LOCALE
): string {
    const path = `/valorant-crosshair/${encodeURIComponent(c.slug || String(c.id))}`;
    return `${SITE_CANONICAL_ORIGIN}${localizedPath(path, locale)}`;
}

/**
 * Server-rendered PNG preview of a crosshair (web/crosshair-image.mjs). Only web/server.mjs
 * serves it — the CRA dev server does not, so the image is broken under `npm start`.
 * Prefix SITE_CANONICAL_ORIGIN where a full URL is needed.
 */
export function crosshairImagePath(c: Pick<ICrosshairConfig, "id" | "slug">): string {
    return `/crosshair-images/${encodeURIComponent(c.slug || String(c.id))}.png`;
}

/** Catalog grid — tells engines the page is a list and what each entry points at. */
export function buildItemListJsonLd(items: ICrosshairConfig[], locale: Locale = DEFAULT_LOCALE): string {
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: items.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: crosshairUrl(c, locale),
            name: c.name,
        })),
    });
}

export function buildCrosshairDetailJsonLd(c: ICrosshairConfig, locale: Locale = DEFAULT_LOCALE): string {
    const url = crosshairUrl(c, locale);
    return JSON.stringify([
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: getUiStrings(locale).detail.breadcrumbRoot,
                    item: `${SITE_CANONICAL_ORIGIN}${localizedPath("/", locale)}`,
                },
                { "@type": "ListItem", position: 2, name: c.name, item: url },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: `${c.name} Valorant crosshair`,
            url,
            description: describeCrosshair(c.name, c.code, getUiStrings(locale).settings).summary,
            image: `${SITE_CANONICAL_ORIGIN}${crosshairImagePath(c)}`,
            about: "Valorant crosshair import code",
            text: c.code,
            isPartOf: { "@type": "WebSite", name: DEFAULT_SITE_NAME, url: SITE_CANONICAL_ORIGIN },
        },
    ]);
}

export function buildWebSiteJsonLd(): string {
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: DEFAULT_SITE_NAME,
        url: SITE_CANONICAL_ORIGIN,
        description:
            "Browse and copy Valorant crosshair codes from pros and the community. Custom crosshair generator, pro crosshairs, and fun crosshairs.",
        // No SearchAction: Google retired the sitelinks search box, and the template only
        // advertised `/?search=` URLs, which crawlers then fetched as thin duplicates.
        publisher: ORGANIZATION,
    });
}

export function buildArticleJsonLd(args: {
    headline: string;
    description: string;
    path: string;
    datePublished: string;
    dateModified: string;
}): string {
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: args.headline,
        description: args.description,
        image: [DEFAULT_OG_IMAGE],
        datePublished: args.datePublished,
        dateModified: args.dateModified,
        author: ORGANIZATION,
        publisher: ORGANIZATION,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_CANONICAL_ORIGIN}${args.path}`,
        },
    });
}
